import {usePublicBrandsQuery} from "@/api/companyApi";
import {useViewerGeoQuery} from "@/api/miscApi";
import BrandExploreCard from "@/components/BrandExplore/BrandExploreCard";
import BrandExploreFilters from "@/components/BrandExplore/BrandExploreFilters";
import EmptyState from "@/components/Campaign/management/components/EmptyState";
import LanguageSelector from "@/components/Common/LanguageSelector";
import {Tooltip} from "@/components/Common/Tooltip";
import SkeletonLoader from "@/components/onboarding/SkeletonLoader";
import useCountries from "@/hooks/useCountries";
import {useInViewWithQuery} from "@/hooks/useInViewWithQuery";
import {BRAND_BIO_TOOLTIP_ID} from "@/store/constants";
import {createFileRoute} from "@tanstack/react-router";
import {zodValidator} from "@tanstack/zod-adapter";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import * as RPNInput from "react-phone-number-input";
import {useDebounce, useTitle} from "react-use";
import {z} from "zod";

const INITIAL_SKELETON_COUNT = 8;
const NEXT_PAGE_SKELETON_COUNT = 4;

export const Route = createFileRoute("/explore-brands")({
  component: BrandExplore,
  validateSearch: zodValidator(
    z.object({
      country: z.string().optional(),
    }),
  ),
});

function BrandExplore() {
  const {t, i18n} = useTranslation("brand_explore");
  useTitle(t("page_title"));
  const {getCountryEnLabelByCode} = useCountries();
  const {data: geo, isPending: isGeoPending} = useViewerGeoQuery();

  const {country: countryParam} = Route.useSearch();
  const navigate = Route.useNavigate();

  const country: RPNInput.Country | null =
    countryParam === undefined
      ? geo?.country && RPNInput.isSupportedCountry(geo.country)
        ? geo.country
        : "CH"
      : countryParam && RPNInput.isSupportedCountry(countryParam)
        ? countryParam
        : null;

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useDebounce(() => setDebouncedSearch(search.trim()), 200, [search]);

  const query = usePublicBrandsQuery({
    company_name: debouncedSearch || undefined,
    country: country ? getCountryEnLabelByCode(country) : undefined,
    queryOptions: {enabled: !isGeoPending},
  });
  const {ref: listRef} = useInViewWithQuery(query);

  const brands = query.data?.results ?? [];

  const renderBrandCardSkeleton = (key: number | string) => (
    <div
      key={key}
      className="border-neutral-grey-light-dark flex flex-col gap-3 rounded-lg border bg-white p-4 shadow-xs">
      <div className="flex items-center gap-3">
        <SkeletonLoader className="size-14 shrink-0 rounded-lg" />
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <SkeletonLoader className="h-4 w-32" />
          <SkeletonLoader className="h-3 w-24" />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <SkeletonLoader className="h-3 w-full" />
        <SkeletonLoader className="h-3 w-3/4" />
      </div>
      <div className="flex flex-wrap gap-1.5">
        <SkeletonLoader className="h-6 w-16 rounded-full" />
        <SkeletonLoader className="h-6 w-20 rounded-full" />
        <SkeletonLoader className="h-6 w-14 rounded-full" />
      </div>
      <div className="mt-auto flex flex-col gap-1.5">
        <SkeletonLoader className="h-3 w-40" />
        <SkeletonLoader className="h-3 w-28" />
      </div>
    </div>
  );

  useEffect(() => {
    const descriptionTag = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = descriptionTag?.content ?? null;
    if (descriptionTag) descriptionTag.content = t("meta_description");

    document.documentElement.lang = i18n.resolvedLanguage === "en" ? "en" : "de";

    const canonical = document.createElement("link");
    canonical.rel = "canonical";
    canonical.href = `${window.location.origin}/explore-brands`;
    document.head.appendChild(canonical);

    return () => {
      if (descriptionTag && previousDescription !== null) descriptionTag.content = previousDescription;
      canonical.remove();
    };
  }, [t, i18n.resolvedLanguage]);

  useEffect(() => {
    if (!brands.length) return;
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: brands.map((brand, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: brand.company_name,
        url: brand.slug ? `${window.location.origin}/join/${brand.slug}` : undefined,
      })),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.seo = "brand-list";
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => script.remove();
  }, [brands]);

  return (
    <>
      <main className="bg-neutral-grey-lightest min-h-screen pb-20">
        <div className="max-w-wide container mx-auto flex flex-col gap-6 px-8 py-10">
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="text-neutral-black text-2.5xl m-0 font-semibold">{t("heading")}</h1>
              <p className="text-neutral-grey-dark m-0 text-sm">{t("subheading")}</p>
            </div>
            <LanguageSelector />
          </div>
          <BrandExploreFilters
            country={country}
            isCountryLoading={isGeoPending}
            onCountryChange={next => navigate({search: prev => ({...prev, country: next ?? ""}), replace: true})}
            onSearchChange={setSearch}
            search={search}
          />
          {query.isPending ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({length: INITIAL_SKELETON_COUNT}).map((_, index) => renderBrandCardSkeleton(index))}
            </div>
          ) : !brands.length ? (
            <EmptyState title={t("no_brands_found")} description={t("no_brands_found_description")} />
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {brands.map(brand => (
                  <BrandExploreCard key={brand.slug} brand={brand} />
                ))}
                {query.isFetching &&
                  !query.isLoading &&
                  Array.from({length: NEXT_PAGE_SKELETON_COUNT}).map((_, index) =>
                    renderBrandCardSkeleton(`skeleton-${index}`),
                  )}
              </div>
              {!query.isLoading && query.hasNextPage && <div key={brands.length} ref={listRef} />}
            </>
          )}
        </div>
      </main>
      <Tooltip
        id={BRAND_BIO_TOOLTIP_ID}
        place="bottom"
        variant="light"
        opacity={1}
        delayShow={200}
        clickable
        offset={6}
        className="border-neutral-grey-light-dark text-neutral-grey-dark z-100 max-w-xs rounded-lg border bg-white px-3 py-2 text-left text-sm leading-5 shadow-lg"
        classNameArrow="border-neutral-grey-light-dark border-r border-b bg-white"
        render={({content}) => <div className="max-h-44 overflow-y-auto text-xs whitespace-pre-line">{content}</div>}
      />
    </>
  );
}
